/**
 * C2 "Who can see what of mine" (/account/sharing) and C5, the join-a-portfolio
 * invitation (/account/sharing?accept=<aggregator id>).
 *
 * Rebuilt whole-screen for Story #5648 build v2 against the approved figure. The
 * previous version kept the old shell and bolted cards onto it, and it described
 * a licensing relationship — who pays for what. Nothing on this screen is about
 * who pays for anything.
 *
 * THE RULE (Rob, 6 Sep 2026): the role a person acts as decides which zoom
 * levels they get; what fills each level is what they own plus what has been
 * shared with them in that role. Being part of a portfolio is a relationship and
 * it shows an organisation NOTHING. Every project an organisation can see is a
 * grant this person made, project by project, and can revoke.
 *
 * The page owns every read and every write; the sections are presentation. That
 * is deliberate: the same grants answer three questions here — the grid, each
 * card's "You share: 3 projects · 2 folders hidden", and the strip's "4 of your
 * 6 projects" — and three components fetching separately is three chances for
 * one screen to contradict itself about who can see what.
 */
import { useMemo } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acceptSponsorship,
  acknowledgeIpArrangement,
  declineDisclosureAdvance,
  declineIpArrangement,
  declinePortfolioInvitation,
  disputeIpArrangement,
  getDisclosureAgreements,
  getPermissions,
  getProject,
  getProjects,
  leavePortfolio,
  listAggregatorGrants,
  listMyIpArrangements,
  listMySponsors,
  listPendingSeats,
  listSeatChanges,
  revokeAggregatorGrant,
  setExtendedVisibility,
} from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import PageHeader from '../../components/ui/PageHeader'
import { NextStepBanner, useDefaultLevels, useSetSpine } from '../../components/spine'
import PortfoliosYoureIn from '../../components/sharing/PortfoliosYoureIn'
import SharingChanges from '../../components/sharing/SharingChanges'
import OrganisationVisibilityTable from '../../components/sharing/OrganisationVisibilityTable'
import { GrantProjectAction } from '../../components/sharing/OrganisationVisibilityActions'
import PortfoliosYouveLeft from '../../components/sharing/PortfoliosYouveLeft'
import IpArrangements from '../../components/sharing/IpArrangements'
import CollaboratorsPanel from '../../components/sharing/CollaboratorsPanel'
import { collaboratorName } from '../../components/sharing/CollaboratorsPanel'
import type { CollaboratorRow } from '../../components/sharing/CollaboratorsPanel'
import DisclosureAgreementsPanel from '../../components/sharing/DisclosureAgreementsPanel'
import type { DisclosureRow } from '../../components/sharing/DisclosureAgreementsPanel'
import PortfolioInvitationOverlay from '../../components/sharing/PortfolioInvitationOverlay'
import {
  buildLeftPortfolios,
  buildPortfolios,
  organisationColumns,
  sharedWithAtLeastOne,
} from '../../components/sharing/sharingModel'
import {
  acknowledgeHeadline, liveByProject, waitingOnMe,
} from '../../components/sharing/ipModel'
import type { ProjectGrants } from '../../components/sharing/sharingModel'
import type { Project } from '../../types'
import type { IpArrangement } from '../../types/ipArrangements'

/** An owner-only refusal on the grants route is the right answer, not a failure. */
function isNotOwnerRefusal(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 403
}

export default function WhoCanSeeWhatOfMine() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const defaultLevels = useDefaultLevels()

  const projectsQuery = useQuery({ queryKey: ['projects'], queryFn: getProjects })
  const projects: Project[] = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data])

  const seatsQuery = useQuery({ queryKey: ['portfolios-im-in'], queryFn: listMySponsors })
  const pendingQuery = useQuery({ queryKey: ['portfolio-invitations'], queryFn: listPendingSeats })
  const changesQuery = useQuery({ queryKey: ['sharing-changes'], queryFn: listSeatChanges })
  const disclosureQuery = useQuery({
    queryKey: ['disclosure-agreements'],
    queryFn: () => getDisclosureAgreements(),
  })
  // The IP arrangements on the caller's own projects. Read once here, like
  // everything else on this screen: the same rows answer three questions — the
  // card, the pill on the grid row, and whether the banner says ACKNOWLEDGE.
  const ipQuery = useQuery({
    queryKey: ['ip-arrangements-mine'],
    queryFn: listMyIpArrangements,
  })

  const grantQueries = useQueries({
    queries: projects.map((p) => ({
      queryKey: ['aggregator-grants', p.project_id],
      queryFn: () => listAggregatorGrants(p.project_id),
      staleTime: 60 * 1000,
      // An owner-refusal is the route's FINAL answer, so retrying cannot change
      // it — it only doubles the window in which the row has nothing to show
      // (Bug #5839). Every other error keeps the app default of one retry.
      retry: (failureCount: number, error: unknown) =>
        !isNotOwnerRefusal(error) && failureCount < 1,
    })),
  })

  const projectGrants: ProjectGrants[] = projects.map((project, i) => {
    const q = grantQueries[i]
    if (!q || q.isPending) return { project, status: 'loading', grants: [] }
    if (q.isError) {
      return { project, status: isNotOwnerRefusal(q.error) ? 'not-owner' : 'error', grants: [] }
    }
    return { project, status: 'ready', grants: q.data?.grants ?? [] }
  })

  const ownedProjects = projectGrants.filter((p) => p.status !== 'not-owner')

  // Collaborators are read only for projects the caller owns — the permissions
  // route refuses the rest, and asking is a request whose answer we already know.
  const permissionQueries = useQueries({
    queries: ownedProjects.map((p) => ({
      queryKey: ['permissions', p.project.project_id],
      queryFn: () => getPermissions(p.project.project_id),
      staleTime: 60 * 1000,
      retry: (failureCount: number, error: unknown) =>
        !isNotOwnerRefusal(error) && failureCount < 1,
    })),
  })

  const collaborators: CollaboratorRow[] = ownedProjects.flatMap((p, i) => {
    const rows = permissionQueries[i]?.data ?? []
    return rows
      // Organisations have their own grid above; a group is a container, not a
      // person; and the owner is not their own collaborator.
      .filter((perm) => (perm.grantee_type ?? 'user') === 'user')
      .filter((perm) => perm.grantee_id !== p.project.owner_id)
      .map((perm) => ({
        projectId: p.project.project_id,
        projectName: p.project.name,
        granteeId: perm.grantee_id,
        grantee: collaboratorName(perm.grantee_display_name, perm.grantee_email),
        level: perm.permission_level,
      }))
  })

  const portfolios = buildPortfolios(
    seatsQuery.data ?? [], pendingQuery.data ?? [], projectGrants,
  )
  const leftPortfolios = buildLeftPortfolios(changesQuery.data ?? [], portfolios, projectGrants)
  const columns = organisationColumns(projectGrants, portfolios)
  const sharedProjects = sharedWithAtLeastOne(projectGrants)

  const projectNameById = new Map(projects.map((p) => [p.project_id, p.name]))

  // A disclosure agreement can name a project that is not in the caller's own
  // list — archived, or held under another workspace — and the card then read
  // "A project of yours" on half of charlie's rows while naming the other half.
  // A screen that names three projects and calls three others "a project" is
  // not answering its own question, so the unnamed ones are looked up by id.
  const unresolvedProjectIds = Array.from(new Set(
    (disclosureQuery.data ?? [])
      .flatMap((a) => [
        a.project_id,
        ...((a as { initiating_project_id?: string }).initiating_project_id
          ? [(a as { initiating_project_id?: string }).initiating_project_id as string] : []),
        ...((a as { related_project_ids?: string[] }).related_project_ids ?? []),
      ])
      .filter((id): id is string => Boolean(id) && !projectNameById.has(id as string)),
  ))
  const extraProjectQueries = useQueries({
    queries: unresolvedProjectIds.map((id) => ({
      queryKey: ['project', id],
      queryFn: () => getProject(id),
      staleTime: 5 * 60 * 1000,
      // A project the caller cannot read answers 403/404 and stays unnamed —
      // that is the correct answer, and retrying cannot change it.
      retry: false,
    })),
  })
  extraProjectQueries.forEach((q, i) => {
    if (q.data?.name) projectNameById.set(unresolvedProjectIds[i], q.data.name)
  })
  const disclosureRows: DisclosureRow[] = (disclosureQuery.data ?? []).map((a) => {
    const ids = [
      a.project_id,
      (a as { initiating_project_id?: string }).initiating_project_id,
      ...((a as { related_project_ids?: string[] }).related_project_ids ?? []),
    ]
    const named = ids.find((id): id is string => Boolean(id) && projectNameById.has(id as string))
    const hasAnyId = ids.some((id) => Boolean(id))
    return {
      agreementId: a.agreement_id,
      // THE FOUR ROWS THAT SAID "A project of yours" HAD NO PROJECT TO NAME.
      // Round 4, measured against the live API on 6 Sep 2026: of charlie's six
      // agreements, the four at rung 3 and above carry project_id /
      // initiating_project_id / related_project_ids and now name their project;
      // the two at rungs 1 and 2 — and bob's and beta's single rows, both at
      // rung 1 — carry NO project key of any kind. There was nothing to look
      // up, and another lookup would have found nothing.
      //
      // That is the ladder working rather than data missing: below rung 3 a
      // disclosure is a blind topic match, bound to no project on either side.
      // So the card says that, instead of "A project of yours" — which reads
      // like a name the screen failed to fetch and quietly implies there is one.
      projectName: named
        ? (projectNameById.get(named) as string)
        : hasAnyId
          // There IS a project, and the caller cannot read it — archived, or
          // held in another workspace. An honest stand-in for a real thing.
          ? 'A project of yours'
          : 'A blind match — no project named yet',
      // Below rung 4 the agreement carries no name and the card says so — an
      // anonymous counterparty is the ladder working, not missing data.
      counterparty: a.counterparty_name ?? null,
      currentLevel: a.current_level,
      advancePending: (a.counterparty_consented_level ?? 0) > (a.my_consented_level ?? 0),
    }
  })

  // ---------------------------------------------------------------------
  // The next step waiting at this level. Real state, in priority order —
  // never a constant, and never a step that is not actually waiting.
  // ---------------------------------------------------------------------
  const invitationsWaiting = portfolios.filter((p) => p.state === 'invited')
  const advancesWaiting = disclosureRows.filter((d) => d.advancePending)
  // An arrangement is waiting on the CALLER only while it is still proposed and
  // they personally have not answered. One that every other owner has already
  // acknowledged still counts — it is theirs to answer.
  const arrangements: IpArrangement[] = ipQuery.data ?? []
  const acknowledgementsWaiting = waitingOnMe(arrangements)
  const stepsWaiting =
    invitationsWaiting.length + advancesWaiting.length + acknowledgementsWaiting.length
  const ipByProject = liveByProject(arrangements)

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ['portfolios-im-in'] })
    qc.invalidateQueries({ queryKey: ['portfolio-invitations'] })
    qc.invalidateQueries({ queryKey: ['sharing-changes'] })
  }

  const handleAccept = async (aggregatorId: string) => {
    await acceptSponsorship(aggregatorId)
    refetchAll()
  }
  const handleDecline = async (aggregatorId: string) => {
    await declinePortfolioInvitation(aggregatorId)
    refetchAll()
  }
  const handleLeave = async (aggregatorId: string) => {
    await leavePortfolio(aggregatorId)
    refetchAll()
  }
  const handleEnableExtendedVisibility = async (aggregatorId: string) => {
    if (!user?.id) return
    // The aggregator id is passed explicitly: with more than one live
    // relationship the route refuses to guess rather than changing whichever
    // row DynamoDB returned first.
    await setExtendedVisibility(user.id, true, 'I understand', aggregatorId)
    refetchAll()
  }
  const handleRevokeAll = async (aggregatorId: string) => {
    const holding = projectGrants.filter((p) =>
      p.grants.some((g) => g.aggregator_id === aggregatorId))
    for (const p of holding) {
      await revokeAggregatorGrant(p.project.project_id, aggregatorId)
    }
    qc.invalidateQueries({ queryKey: ['aggregator-grants'] })
  }
  const handleHoldDisclosure = async (agreementId: string) => {
    await declineDisclosureAdvance(agreementId)
    qc.invalidateQueries({ queryKey: ['disclosure-agreements'] })
  }
  // Answering an arrangement changes only the arrangement. Nothing else on this
  // screen is invalidated because nothing else can have changed: the route
  // cannot write a grant, and the grid is grants.
  const refetchArrangements = () => {
    qc.invalidateQueries({ queryKey: ['ip-arrangements-mine'] })
  }
  const handleAcknowledgeIp = async (arrangementId: string) => {
    await acknowledgeIpArrangement(arrangementId)
    refetchArrangements()
  }
  const handleDeclineIp = async (arrangementId: string) => {
    await declineIpArrangement(arrangementId)
    refetchArrangements()
  }
  const handleDisputeIp = async (arrangementId: string) => {
    await disputeIpArrangement(arrangementId)
    refetchArrangements()
  }


  // The rail holds at My inventions: this is a view of everything the caller
  // owns, and there is nothing wider of their own to zoom out to.
  useSetSpine({
    level: 'my-inventions',
    // The viewer's own role decides the notches; C2 is an inventor's screen and
    // the figure draws the four inventor ones.
    levels: defaultLevels,
    youreAt: ['Sharing'],
    stepsWaiting,
    zoomIn: projects[0]
      ? { title: `Zoom in → Invention — ${projects[0].name}`, to: `/projects/${projects[0].project_id}` }
      : { title: 'Zoom in → Invention' },
    zoomOut: { title: 'This is the widest view of your own inventions' },
    crumb: ['Personal', 'Sharing'],
  })

  // The cards' "You share: N projects · M folders hidden" and the strip's "4 of
  // your 6 projects" are read off the grants, so this section is not loaded until
  // the grants are. Showing "0 projects" while they are still arriving would be
  // the same lie as showing "Nothing granted" mid-flight, and on this screen a
  // reassuring number that turns out to be wrong is the worst kind.
  const grantsSettled = grantQueries.every((q) => !q.isPending)
  const portfoliosLoaded =
    !seatsQuery.isPending && !pendingQuery.isPending
    && !projectsQuery.isPending && grantsSettled
  const portfoliosErrored = seatsQuery.isError || pendingQuery.isError
  const anyGrantErrored = grantQueries.some((q) => q.isError && !isNotOwnerRefusal(q.error))


  return (
    <div>
      <PageHeader
        title="Who can see what of mine"
        description="Every organisation with a view into your inventions — what they can see, what you hid, which portfolios you are part of, and what nobody can do."
        helpKey="account-sharing"
        primaryAction={
          <GrantProjectAction
            projects={ownedProjects.map((p) => ({
              projectId: p.project.project_id, name: p.project.name,
            }))}
            organisations={columns.map((c) => ({ id: c.id, name: c.name }))}
          />
        }
      />

      {/* The next step waiting at this level, from live state.

          ACKNOWLEDGE ranks first, and the figure is why: C2 draws an
          outstanding invitation (Coastal Ventures, "Invited · not yet
          accepted") AND an IP proposal at the same time, and the banner it
          shows is the ACKNOWLEDGE one. An unanswered proposal is also the more
          consequential of the two — it is a legal record with the caller's name
          on it — where an invitation left unanswered shows nobody anything. */}
      {acknowledgementsWaiting.length > 0 ? (
        <NextStepBanner
          at="MY INVENTIONS"
          verb="ACKNOWLEDGE"
          headline={acknowledgeHeadline(acknowledgementsWaiting[0])}
          rule="from your IP arrangements · a proposal changes nothing until you acknowledge it"
          cta={{
            label: 'Read the proposal →',
            onClick: () => document
              .getElementById('ip-arrangements')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          }}
        />
      ) : invitationsWaiting.length > 0 ? (
        <NextStepBanner
          at="MY INVENTIONS"
          verb="ACCEPT"
          headline={`${invitationsWaiting[0].organisation} invites you to join its portfolio — accept it, or decline.`}
          rule="from your invitations · joining shows them nothing until you grant a project"
          cta={{ label: 'Read the invitation →', to: `/account/sharing?accept=${invitationsWaiting[0].aggregatorId}` }}
        />
      ) : advancesWaiting.length > 0 ? (
        <NextStepBanner
          at="MY INVENTIONS"
          verb="CONSENT"
          headline={`A disclosure on '${advancesWaiting[0].projectName}' is waiting for you at rung ${advancesWaiting[0].currentLevel} — consent to advance it, or hold it here.`}
          rule="from your disclosure agreements · nothing advances until you consent"
          /* The figure's banner always carries an action; this one had none, so
             it named a step and left the reader to go and find it. It scrolls to
             the agreement it is about — the one place where consenting and
             holding both actually live. */
          cta={{
            label: 'Open the agreement →',
            onClick: () => document
              .querySelector('[data-part="DisclosureAgreementsPanel"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          }}
        />
      ) : (
        <NextStepBanner
          at="MY INVENTIONS"
          // `none` renders "Nothing waiting at MY INVENTIONS" and drops the
          // verb, so there is no verb to give — the skeleton still requires the
          // prop, and an invented one would print nowhere and mislead a reader
          // of this file.
          verb=""
          none
          headline="Nothing is waiting on you here. No invitation to answer, no disclosure asking to advance, and no IP arrangement to acknowledge."
          rule="from your invitations, disclosure agreements and IP arrangements"
        />
      )}

      {projectsQuery.isError && (
        <p className="mb-4 text-sm text-rose-600">
          Could not load your projects — the grid below is not a statement about
          your sharing until it loads.
        </p>
      )}

      <PortfoliosYoureIn
        portfolios={portfolios}
        loaded={portfoliosLoaded}
        error={portfoliosErrored}
        onAccept={handleAccept}
        onDecline={handleDecline}
        onLeave={handleLeave}
        onEnableExtendedVisibility={handleEnableExtendedVisibility}
        sharedProjects={sharedProjects}
      />

      <SharingChanges
        changes={changesQuery.data ?? []}
        loaded={!changesQuery.isPending}
        error={changesQuery.isError}
      />

      <OrganisationVisibilityTable
        projects={projectGrants}
        columns={columns}
        anyErrored={anyGrantErrored}
        ipByProject={ipByProject}
      />

      <div className="mb-4 grid items-start gap-3.5 lg:grid-cols-2">
        <PortfoliosYouveLeft
          portfolios={leftPortfolios}
          loaded={!changesQuery.isPending}
          onRevokeAll={handleRevokeAll}
        />
        <IpArrangements
          arrangements={arrangements}
          loaded={!ipQuery.isPending}
          error={ipQuery.isError}
          onAcknowledge={handleAcknowledgeIp}
          onDecline={handleDeclineIp}
          onDispute={handleDisputeIp}
        />
      </div>

      <div className="mb-4 grid items-start gap-3.5 lg:grid-cols-2">
        <CollaboratorsPanel
          collaborators={collaborators}
          loaded={permissionQueries.every((q) => !q.isPending)}
          error={permissionQueries.some((q) => q.isError && !isNotOwnerRefusal(q.error))}
        />
        <DisclosureAgreementsPanel
          agreements={disclosureRows}
          loaded={!disclosureQuery.isPending}
          error={disclosureQuery.isError}
          onHold={handleHoldDisclosure}
        />
      </div>

      <div className="rounded-xl border-l-4 border-l-iflow-500 border border-iflow-500/25 bg-iflow-50/70 px-4 py-3 text-[12.5px] leading-relaxed text-slate-700">
        <b className="text-slate-900">What nobody can do.</b> No portfolio can
        read a project you haven&apos;t granted. A portfolio can never turn
        extended visibility on. No organisation can narrow what you see of your
        own inventions. Platform staff cannot open your files or read your
        claims. Blind matching shares topic overlap only until you consent.
      </div>

      {/* C5 — the invitation. It follows the inventor rather than living on one
          page: the same overlay is mounted on Home, which is where figure C5
          draws it, and it renders nothing at all without ?accept. */}
      <PortfolioInvitationOverlay />
    </div>
  )
}
